import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '@hooks/useTranslation';
import { useUserRole } from '@hooks/useUserRole';
import { Tabs } from '@components/common';
import MyBorrowingsPage from '../MyBorrowingsPage';
import AllBorrowingsPage from '../AllBorrowingsPage';
import BorrowingsCalendarPage from '../BorrowingsCalendarPage';
import PendingValidationsPage from '../PendingValidationsPage/PendingValidationsPage';
import './BorrowingsPage.scss';

export const BorrowingsPage = () => {
    const { t } = useTranslation();
    const { hasAdminPrivileges } = useUserRole();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('my-borrowings');

    // Sync activeTab with current route
    useEffect(() => {
        if (location.pathname.includes('/my-borrowings')) {
            setActiveTab('my-borrowings');
        } else if (location.pathname.includes('/all-borrowings')) {
            // Only allow access to all-borrowings for admin users
            if (hasAdminPrivileges) {
                setActiveTab('all-borrowings');
            } else {
                // Redirect non-admin users to my-borrowings
                navigate('/borrowings/my-borrowings', { replace: true });
                setActiveTab('my-borrowings');
            }
        } else if (location.pathname.includes('/calendar')) {
            setActiveTab('calendar');
        } else if (location.pathname.includes('/pending-validations')) {
            setActiveTab('pending-validations');
        } else if (location.pathname === '/borrowings' || location.pathname === '/borrowings/') {
            // Default to my-borrowings for base borrowings path
            setActiveTab('my-borrowings');
        }
    }, [location.pathname, hasAdminPrivileges, navigate]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        navigate(`/borrowings/${tabId}`);
    };

    const tabs = [
        {
            id: 'my-borrowings',
            label: t('borrowing.myBorrowings'),
            content: <MyBorrowingsPage />
        },
        {
            id: 'calendar',
            label: t('borrowing.calendar.tabLabel'),
            content: <BorrowingsCalendarPage />
        },
        {
            id: 'pending-validations',
            label: t('borrowing.validation.tabLabel') || 'Validations',
            content: <PendingValidationsPage />
        },
        // Only show All Borrowings tab for admin users
        ...(hasAdminPrivileges ? [{
            id: 'all-borrowings',
            label: t('borrowing.allBorrowings'),
            content: <AllBorrowingsPage />
        }] : []),
    ];

    return (
        <div className="borrowings-page">
            <div className="borrowings-page__header">
                <h1 className="borrowings-page__title">{t('borrowing.page_title')}</h1>
                <p className="borrowings-page__description">
                    {t('borrowing.page_description')}
                </p>
            </div>

            <div className="borrowings-page__content">
                <Tabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    variant="underline"
                    className="borrowings-tabs"
                />
            </div>
        </div>
    );
};

export default BorrowingsPage;
